/**
 * MONSTER TOWN — TOWN PROPS THE KID PLACES. WORLD CREW's content drop.
 *
 * The nature-and-street half of the build palette: trees, bushes, rocks, lamp
 * posts, benches, planters, a mailbox, a picture signpost and pond water. The
 * build crew's kit owns walls/roofs/floors/paths/fences; these are the things
 * that make a town feel *lived in* rather than constructed.
 *
 * Everything here is DATA. No engine file changed to add any of it, and every
 * single one reacts when a kid taps it (LAW #1 — the compiler and `npm run
 * audit` both check). The art mirrors `src/world/props.ts` so a tree the kid
 * plants sits happily next to the ones that were already in the town.
 */

import { FOOT_1x1, FOOT_2x1, type Cell } from '../core/grid'
import type { PaletteName } from '../core/palette'
import { definePlaceable } from '../core/placeable'
import type { PlaceableId } from '../core/types'
import { ball, box, boxOn, cone, cylinder, flat, group, inkAll, inked } from '../render/kit'

// ---------------------------------------------------------------------------
// nature
// ---------------------------------------------------------------------------

export const treeOak = definePlaceable<{ shakes: number }>({
  id: 'tree_oak' as PlaceableId,
  devName: 'Round tree',
  category: 'tree',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['leaf', 'leafLight', 'leafDark', 'flowerPink'],
  tags: ['starter', 'nature'],
  initialState: () => ({ shakes: 0 }),

  /*
   * ONE TREE FAMILY — see the header note in `src/world/props.ts`.
   *
   * ROUND-3: "four different art languages for 'tree' in one screenshot". Two of
   * the four were here rather than in the scenery: a kid-planted oak whose trunk
   * was a 7-segment cylinder and whose crown was inked on the big box only (the
   * cap on top carried no line at all — a partial outline reads as a rendering
   * fault, not a style), standing beside a scenery tree that is boxes all the way
   * down and inked all the way round. Same vocabulary now: box trunk, stepped
   * box crown, `inkAll` over the whole thing.
   */
  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('tree_oak')
    const h = t * (0.9 + ctx.variety * 0.3)
    const twist = ctx.variety * 0.9
    const trunk = boxOn(t * 0.3, h, t * 0.3, { color: 'trunk' })
    trunk.rotation.y = twist
    g.add(trunk)
    const flare = boxOn(t * 0.42, t * 0.12, t * 0.42, { color: 'trunkDark' })
    flare.rotation.y = twist
    g.add(flare)
    const crown = group('crown')
    const body = boxOn(t * 1.3, t * 0.8, t * 1.3, { color: ctx.tint ?? 'leaf', y: h - t * 0.1 })
    body.rotation.y = twist
    crown.add(body)
    const cap = boxOn(t * 0.85, t * 0.55, t * 0.85, { color: 'leafLight', y: h + t * 0.6 })
    cap.rotation.y = 0.4 - twist
    crown.add(cap)
    g.add(crown)
    return inkAll(g)
  },

  // Shake the tree: it wobbles, leaves come down, the monster gets a giggle.
  interact: (ctx) => {
    const shakes = ctx.state.shakes + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'wobble', seconds: 0.6, target: 'crown' },
        { kind: 'particles', effect: 'leaves', count: 9 },
        { kind: 'monster', action: 'shake', seconds: 0.8 },
        { kind: 'sound', cue: 'leaves_rustle' },
        { kind: 'state', patch: { shakes } },
      ],
      cooldownMs: 260,
    }
  },
})

export const treePine = definePlaceable<{ shakes: number }>({
  id: 'tree_pine' as PlaceableId,
  devName: 'Pointy tree',
  category: 'tree',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['leafDark', 'leaf', 'wallMint'],
  tags: ['nature'],
  initialState: () => ({ shakes: 0 }),

  /*
   * STEPPED BOXES, NOT STACKED CONES. This was three smooth 8-segment cones —
   * the "stepped-pyramid conifer" the round-3 critic counted as a separate art
   * language from the scenery pine standing behind it, which is four stepped
   * boxes. Now they are the same tree at two sizes.
   */
  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('tree_pine')
    const h = t * (1.5 + ctx.variety * 0.4)
    const twist = ctx.variety * 0.8
    const trunk = boxOn(t * 0.24, h * 0.3, t * 0.24, { color: 'trunk' })
    trunk.rotation.y = twist
    g.add(trunk)
    const flare = boxOn(t * 0.34, t * 0.1, t * 0.34, { color: 'trunkDark' })
    flare.rotation.y = twist
    g.add(flare)
    const crown = group('crown')
    const steps: readonly [number, number, PaletteName][] = [
      [0.98, 0.16, 'leafDark'],
      [0.74, 0.42, 'leaf'],
      [0.5, 0.66, 'leafLight'],
      [0.28, 0.86, 'leaf'],
    ]
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i]
      if (s === undefined) continue
      const m = boxOn(t * s[0], h * 0.3, t * s[0], {
        color: ctx.tint ?? s[2],
        y: h * s[1],
      })
      m.rotation.y = twist + i * 0.22
      crown.add(m)
    }
    g.add(crown)
    return inkAll(g)
  },

  interact: (ctx) => {
    const shakes = ctx.state.shakes + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'wobble', seconds: 0.5, target: 'crown' },
        { kind: 'particles', effect: shakes % 3 === 0 ? 'snowPuff' : 'leaves', count: 7 },
        { kind: 'sound', cue: 'leaves_rustle' },
        { kind: 'state', patch: { shakes } },
      ],
      cooldownMs: 260,
    }
  },
})

export const bushRound = definePlaceable<{ hides: number }>({
  id: 'plant_bush' as PlaceableId,
  devName: 'Bush',
  category: 'plant',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['leaf', 'leafLight', 'flowerPink', 'flowerYellow'],
  tags: ['starter', 'nature'],
  initialState: () => ({ hides: 0 }),

  /*
   * TWO STEPPED BOXES, matching `bush()` in the scenery kit. Three overlapping
   * spheres is the "blobby pale-green sphere cluster" the round-3 critic named,
   * and it looked like a different game from the boxes it was planted next to.
   */
  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('plant_bush')
    const w = t * 0.78
    const h = t * 0.36
    const body = group('body')
    const base = boxOn(w, h, w * 0.88, { color: ctx.tint ?? 'leaf' })
    base.rotation.y = ctx.variety * 0.9
    body.add(base)
    const cap = boxOn(w * 0.66, h * 0.62, w * 0.6, { color: 'leafLight', y: h - t * 0.03 })
    cap.rotation.y = 0.5 - ctx.variety * 0.8
    body.add(cap)
    g.add(body)
    return inkAll(g)
  },

  // Rummage in the bush — it rustles and sometimes a butterfly comes out.
  interact: (ctx) => {
    const hides = ctx.state.hides + 1
    const surprise = hides % 4 === 0
    return {
      feedback: [
        { kind: 'anim', clip: 'wobble', seconds: 0.45, target: 'body' },
        { kind: 'particles', effect: surprise ? 'butterflies' : 'leaves', count: surprise ? 5 : 4 },
        { kind: 'sound', cue: 'leaves_rustle' },
        { kind: 'state', patch: { hides } },
      ],
      cooldownMs: 200,
    }
  },
})

export const rockStone = definePlaceable<{ turns: number }>({
  id: 'rock_stone' as PlaceableId,
  devName: 'Rock',
  category: 'decor',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: true,
  walkable: false,
  tints: ['rock', 'rockDark', 'sand'],
  tags: ['starter', 'nature'],
  initialState: () => ({ turns: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('rock_stone')
    const body = group('body')
    const main = boxOn(t * 0.7, t * 0.45, t * 0.55, { color: ctx.tint ?? 'rock' })
    main.rotation.y = ctx.variety * 1.3
    body.add(inked(main))
    body.add(
      boxOn(t * 0.36, t * 0.24, t * 0.32, {
        color: 'rockDark',
        x: t * 0.26,
        z: t * 0.16,
        rotY: -ctx.variety,
      }),
    )
    g.add(body)
    return g
  },

  // Roll the rock over — a beetle scuttles out. Kids do this in real gardens.
  interact: (ctx) => {
    const turns = ctx.state.turns + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'nudge', seconds: 0.5, target: 'body' },
        { kind: 'particles', effect: 'dustPuff', count: 5 },
        { kind: 'spawn', what: 'beetle' },
        { kind: 'sound', cue: 'stone_scrape' },
        { kind: 'state', patch: { turns } },
      ],
      cooldownMs: 260,
    }
  },
})

export const pondWater = definePlaceable<{ splashes: number }>({
  id: 'water_pond' as PlaceableId,
  devName: 'Pond water',
  category: 'water',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  connect: { group: 'water', style: 'edge' },
  tints: ['water', 'waterDeep'],
  tags: ['starter', 'nature'],
  initialState: () => ({ splashes: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('water_pond')
    /*
     * NO CROSS SEAM. The surface used to be t*0.98 wide, which left a 2%
     * channel of bare ground showing between neighbouring tiles: a 2x2 pond
     * rendered as "a flat blue square with a visible cross-shaped seam
     * splitting it into four quads". A tiling surface has to be EXACTLY one
     * tile, and the depth variation has to come from the connect mask instead.
     */
    const dirs: readonly [number, number, number][] = [
      [1, 0, -1],
      [2, 1, 0],
      [4, 0, 1],
      [8, -1, 0],
    ]
    // Sandy shore only where there is NO neighbouring water — a pond a kid
    // paints out tile by tile still comes out looking like one pond. LAW #5.
    for (const [bit, dx, dz] of dirs) {
      if ((ctx.connect & bit) !== 0) continue
      g.add(
        boxOn(dx === 0 ? t * 1.3 : t * 0.34, t * 0.09, dz === 0 ? t * 1.3 : t * 0.34, {
          color: 'sand',
          x: (dx * t) / 2,
          z: (dz * t) / 2,
        }),
      )
    }
    // Outer corners too, from the 8-bit mask, so an L-shaped shore has no notch.
    const corners: readonly [number, number, number, number, number][] = [
      [16, 1, 2, 1, -1],
      [32, 32, 2, 1, 1],
      [64, 64, 8, -1, 1],
      [128, 128, 1, -1, -1],
    ]
    for (const [diag, , , sx, sz] of corners) {
      if ((ctx.connect8 & diag) !== 0) continue
      g.add(
        boxOn(t * 0.34, t * 0.09, t * 0.34, {
          color: 'sand',
          x: (sx * t) / 2,
          z: (sz * t) / 2,
        }),
      )
    }
    const surface = boxOn(t, t * 0.08, t, {
      color: ctx.tint ?? 'water',
      y: 0.02,
      name: 'surface',
    })
    g.add(surface)
    /*
     * Depth reads from HOW ENCLOSED the tile is, not from a single all-four
     * test. A 2x2 pond has no tile with all four neighbours, so the old rule
     * never fired once and every painted pond was uniformly flat cyan.
     */
    const neighbours =
      ((ctx.connect & 1) !== 0 ? 1 : 0) +
      ((ctx.connect & 2) !== 0 ? 1 : 0) +
      ((ctx.connect & 4) !== 0 ? 1 : 0) +
      ((ctx.connect & 8) !== 0 ? 1 : 0)
    if (neighbours >= 2) {
      const inset = neighbours >= 4 ? 1 : 0.72
      g.add(boxOn(t * inset, t * 0.06, t * inset, { color: 'waterDeep', y: 0.08 }))
    }
    if (neighbours >= 3) g.add(boxOn(t * 0.5, t * 0.05, t * 0.5, { color: 'shadow', y: 0.13 }))
    return g
  },

  interact: (ctx) => {
    const splashes = ctx.state.splashes + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'ripple', seconds: 0.9, target: 'surface' },
        { kind: 'particles', effect: 'splash', count: 10 },
        { kind: 'monster', action: 'splash', seconds: 1 },
        { kind: 'sound', cue: 'water_plop' },
        { kind: 'state', patch: { splashes } },
      ],
      cooldownMs: 200,
    }
  },
})

// ---------------------------------------------------------------------------
// street furniture
// ---------------------------------------------------------------------------

export const townLamp = definePlaceable<{ on: boolean }>({
  id: 'town_lamp' as PlaceableId,
  devName: 'Lamp post',
  category: 'light',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 30,
  stackable: false,
  walkable: false,
  tints: ['metalDark', 'roofTeal', 'roofRed', 'roofPurple'],
  tags: ['street'],
  initialState: () => ({ on: true }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('town_lamp')
    g.add(boxOn(t * 0.32, t * 0.12, t * 0.32, { color: 'rockDark' }))
    g.add(cylinder(t * 0.07, t * 1.7, { color: ctx.tint ?? 'metalDark', y: t * 0.1, seg: 6 }))
    g.add(boxOn(t * 0.26, t * 0.11, t * 0.26, { color: ctx.tint ?? 'metalDark', y: t * 1.78 }))
    const bulb = boxOn(t * 0.33, t * 0.3, t * 0.33, { color: 'sunWarm', y: t * 1.87, name: 'bulb' })
    bulb.material = flat('sunWarm')
    bulb.castShadow = false
    g.add(bulb)
    g.add(cone(t * 0.25, t * 0.2, { color: ctx.tint ?? 'metalDark', y: t * 2.18, seg: 6 }))
    return g
  },

  interact: (ctx) => {
    const on = !ctx.state.on
    return {
      feedback: [
        { kind: 'toggle', on, target: 'bulb' },
        { kind: 'particles', effect: on ? 'sparkle' : 'poof', count: 6 },
        { kind: 'sound', cue: on ? 'click_on' : 'click_off' },
        { kind: 'state', patch: { on } },
      ],
    }
  },
})

export const townBench = definePlaceable<{ sits: number }>({
  id: 'town_bench' as PlaceableId,
  devName: 'Bench',
  category: 'furniture',
  scale: 'both',
  footprint: FOOT_2x1,
  cost: 25,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wood', 'wallMint', 'wallBlue'],
  tags: ['street', 'cosy'],
  initialState: () => ({ sits: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('town_bench')
    const seat = group('seat')
    for (const x of [-t * 0.62, t * 0.62]) {
      seat.add(boxOn(t * 0.12, t * 0.26, t * 0.5, { color: 'metalDark', x }))
    }
    seat.add(inked(boxOn(t * 1.6, t * 0.11, t * 0.5, { color: ctx.tint ?? 'woodLight', y: t * 0.26 })))
    const back = boxOn(t * 1.6, t * 0.36, t * 0.1, {
      color: ctx.tint ?? 'woodLight',
      y: t * 0.34,
      z: -t * 0.22,
    })
    back.rotation.x = -0.12
    seat.add(back)
    g.add(seat)
    return g
  },

  // Sit down. The monster flops onto it and the bench creaks.
  interact: (ctx) => {
    const sits = ctx.state.sits + 1
    return {
      feedback: [
        { kind: 'monster', action: 'sit', seconds: 2.4 },
        { kind: 'anim', clip: 'settle', seconds: 0.5, target: 'seat' },
        { kind: 'particles', effect: 'hearts', count: 4 },
        { kind: 'sound', cue: 'bench_creak' },
        { kind: 'state', patch: { sits } },
      ],
      cooldownMs: 400,
    }
  },
})

export const townPlanter = definePlaceable<{ watered: number }>({
  id: 'plant_planter' as PlaceableId,
  devName: 'Flower box',
  category: 'plant',
  scale: 'both',
  footprint: FOOT_2x1,
  cost: 15,
  stackable: true,
  walkable: false,
  tints: ['flowerPink', 'flowerYellow', 'flowerPurple', 'flowerRed', 'flowerWhite'],
  tags: ['cosy'],
  initialState: () => ({ watered: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('plant_planter')
    g.add(inked(boxOn(t * 1.7, t * 0.32, t * 0.7, { color: 'wood' })))
    g.add(boxOn(t * 1.5, t * 0.08, t * 0.55, { color: 'dirtDark', y: t * 0.3 }))
    const heads = group('heads')
    for (const x of [-t * 0.5, 0, t * 0.5]) {
      heads.add(ball(t * 0.16, { color: 'leaf', x, y: t * 0.44, seg: 7 }))
      heads.add(ball(t * 0.1, { color: ctx.tint ?? 'flowerPink', x, y: t * 0.56, seg: 7 }))
    }
    g.add(heads)
    return g
  },

  interact: (ctx) => {
    const watered = ctx.state.watered + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'bob', seconds: 0.9, target: 'heads' },
        { kind: 'particles', effect: 'petals', count: 8 },
        { kind: 'monster', action: 'sniff', seconds: 1.1 },
        { kind: 'sound', cue: 'sparkle_soft' },
        { kind: 'state', patch: { watered } },
      ],
    }
  },
})

export const townMailbox = definePlaceable<{ flag: boolean; opens: number }>({
  id: 'town_mailbox' as PlaceableId,
  devName: 'Mailbox',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 20,
  stackable: false,
  walkable: false,
  tints: ['roofTeal', 'roofRed', 'roofPurple', 'roofMustard'],
  tags: ['street'],
  initialState: () => ({ flag: true, opens: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('town_mailbox')
    g.add(cylinder(t * 0.07, t * 0.6, { color: 'woodDark', seg: 6 }))
    const boxPart = group('boxPart')
    boxPart.add(inked(box(t * 0.34, t * 0.28, t * 0.46, { color: ctx.tint ?? 'roofTeal', y: t * 0.76 })))
    g.add(boxPart)
    g.add(box(t * 0.06, t * 0.2, t * 0.06, { color: 'uiDanger', x: t * 0.2, y: t * 0.88, name: 'flag' }))
    return g
  },

  // Open the mailbox: the flag flips and something is always inside.
  interact: (ctx) => {
    const flag = !ctx.state.flag
    const opens = ctx.state.opens + 1
    return {
      feedback: [
        { kind: 'toggle', on: flag, target: 'flag' },
        { kind: 'anim', clip: 'nudge', seconds: 0.4, target: 'boxPart' },
        { kind: 'particles', effect: 'stars', count: 7 },
        { kind: 'sound', cue: 'mail_open' },
        { kind: 'state', patch: { flag, opens } },
      ],
      cooldownMs: 300,
    }
  },
})

export const townSign = definePlaceable<{ spins: number }>({
  id: 'town_sign' as PlaceableId,
  devName: 'Picture sign',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 18,
  stackable: false,
  walkable: false,
  tints: ['wallCream', 'wallMint', 'wallBlue', 'wallPink'],
  tags: ['street'],
  initialState: () => ({ spins: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('town_sign')
    g.add(cylinder(t * 0.08, t * 1.05, { color: 'wood', seg: 6 }))
    const board = group('board')
    board.add(inked(box(t * 0.95, t * 0.72, t * 0.12, { color: ctx.tint ?? 'wallCream', y: t * 1.2 })))
    // A little picture, never a word (LAW #4).
    board.add(box(t * 0.2, t * 0.3, t * 0.16, { color: 'doorWood', y: t * 1.12, z: t * 0.06 }))
    board.add(cone(t * 0.34, t * 0.24, { color: 'roofRed', y: t * 1.3, z: t * 0.06, seg: 4 }))
    g.add(board)
    return g
  },

  // Spin the sign. It whirls, and points somewhere new every time.
  interact: (ctx) => {
    const spins = ctx.state.spins + 1
    return {
      feedback: [
        { kind: 'anim', clip: 'spin', seconds: 0.8, target: 'board' },
        { kind: 'particles', effect: 'sparkle', count: 5 },
        { kind: 'sound', cue: 'wood_clack' },
        { kind: 'state', patch: { spins } },
      ],
      cooldownMs: 300,
    }
  },
})

/** Everything this crew adds to the palette. Spread into `ALL_CONTENT`. */
export const TOWN_PROPS = [
  treeOak,
  treePine,
  bushRound,
  rockStone,
  pondWater,
  townLamp,
  townBench,
  townPlanter,
  townMailbox,
  townSign,
]

/** Ids that must stay free forever even though they are not a free category. */
export const TOWN_FREE_IDS: PlaceableId[] = [
  treeOak.id,
  treePine.id,
  bushRound.id,
  rockStone.id,
  pondWater.id,
]

/** A little starter garden, for the harness demo. Never auto-placed in a save. */
export function townPropDemo(): { defId: PlaceableId; anchor: Cell }[] {
  return [
    { defId: treeOak.id, anchor: { gx: 3, gy: 0, gz: 3 } },
    { defId: treeOak.id, anchor: { gx: 4, gy: 0, gz: 5 } },
    { defId: treePine.id, anchor: { gx: 2, gy: 0, gz: 6 } },
    { defId: bushRound.id, anchor: { gx: 5, gy: 0, gz: 3 } },
    { defId: rockStone.id, anchor: { gx: 6, gy: 0, gz: 5 } },
    { defId: townBench.id, anchor: { gx: 8, gy: 0, gz: 12 } },
    { defId: townLamp.id, anchor: { gx: 11, gy: 0, gz: 12 } },
    { defId: townPlanter.id, anchor: { gx: 14, gy: 0, gz: 12 } },
    { defId: townMailbox.id, anchor: { gx: 16, gy: 0, gz: 12 } },
    { defId: townSign.id, anchor: { gx: 18, gy: 0, gz: 12 } },
    { defId: pondWater.id, anchor: { gx: 18, gy: 0, gz: 18 } },
    { defId: pondWater.id, anchor: { gx: 19, gy: 0, gz: 18 } },
    { defId: pondWater.id, anchor: { gx: 18, gy: 0, gz: 19 } },
    { defId: pondWater.id, anchor: { gx: 19, gy: 0, gz: 19 } },
  ]
}
